import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Breadcrumbs, P, H6 } from "../../../../../AbstractElements";
import Cleave from "cleave.js/react";
import { useState, useEffect, Fragment } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import SvgIcon from "../../../../CommonComponents/SvgIcon/SvgIcon";
import Dropzone from "react-dropzone";

// COMPANY MASTER
import {
  COMPANY_QUERY_KEY,
  VIEW_COMPANY_QUERY_KEY,
  companyprofileService,
} from "../../../../../Services/companyProfileService";

//INTERFACE
interface UpdateFormProps {
  toggle?: () => void;
  company_code: string;
}

const INITIAL_STATE = {
  company_code: "",
  logo_url: "",
};

const UpdateCompanyNameForm: React.FC<UpdateFormProps> = ({
  toggle,
  company_code,
}) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);

  //Logo Upload
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const onDrop = (acceptedFiles: File[]) =>
    setUploadedFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  const removeFile = (indexToRemove: number) =>
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove),
    );

  const [formData, setFormData] = useState({
    company_code: company_code,
    logo_url: "",
  });

  //FETCH COMPANY DETAILS
  const { data: CompanyData, isLoading: isFetching } = useQuery({
    queryKey: [COMPANY_QUERY_KEY, company_code],
    queryFn: () => companyprofileService.getCompanyProfile(company_code),
  });

  useEffect(() => {
    if (CompanyData) {
      setFormData({
        company_code: CompanyData.data.company_code || company_code,
        logo_url: CompanyData.data.log_url || "",
      });
    }
  }, [CompanyData]);

  const updateCompanyMutation = useMutation<
    any,
    any, // error type
    FormData // input type for .mutate()
  >({
    mutationFn: (formData: FormData) =>
      companyprofileService.updateCompanyLogo(company_code, formData), // pass FormData
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      toast.success("Company Logo was updated successfully!");
      setFormData(INITIAL_STATE);
      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      toast.error(responseData?.message ?? "Failed to update client profile.");
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const data = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        data.append(key, String(value));
      }
    });

    // Append logo file if exists
    if (uploadedFiles.length > 0) {
      data.append("logo_url", uploadedFiles[0]);
      data.append("_method", "PATCH");
    }
    // Trigger mutation
    updateCompanyMutation.mutate(data);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <H6 className="fw-bold">Company Logo</H6>
          <Col md="12">
            <Fragment>
              <Dropzone
                onDrop={onDrop}
                maxFiles={1} // allow only one file
                accept={{
                  "image/jpeg": [".jpeg", ".jpg"],
                  "image/png": [".png"],
                }}
              >
                {({ getRootProps, getInputProps }) => (
                  <div {...getRootProps()} className="dropzone-container">
                    <input {...getInputProps()} />
                    {uploadedFiles.length === 0 ? (
                      <div className="dz-message needsclick">
                        <SvgIcon
                          iconId="file-upload1"
                          style={{ width: 24, height: 24 }}
                        />
                        <H6>
                          Drag File or Click to Browse (JPEG, JPG, PNG only)
                        </H6>
                      </div>
                    ) : (
                      <div className="uploaded-files">
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="file-card">
                            {file.type.startsWith("image/") ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="file-thumbnail"
                              />
                            ) : (
                              <div className="file-placeholder">
                                {file.name.split(".").pop()?.toUpperCase()} File
                              </div>
                            )}
                            <p className="file-name">{file.name}</p>
                            <p className="file-size">
                              {(file.size / 1024).toFixed(2)} KB
                            </p>
                            <button
                              onClick={() => removeFile(index)}
                              className="remove-button"
                              title="Remove file"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Dropzone>
            </Fragment>
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={updateCompanyMutation.isPending}
            >
              {updateCompanyMutation.isPending
                ? "Updating Client Profile..."
                : "Update"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UpdateCompanyNameForm;
